/**
 * Backend Task Firestore Converter (Client SDK)
 *
 * Client-side converter for use with firebase/firestore (NOT firebase-admin).
 * Converts between ContentAgentTask TypeScript type and Firestore data.
 *
 * Handles Date↔Timestamp conversion for date fields.
 * Compatible with legacy frontend/src/contexts/TaskContext.js
 *
 * Collection path: projects/{projectId}/backend_tasks
 */

import {
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
  DocumentData,
  WithFieldValue,
  PartialWithFieldValue,
  SetOptions,
} from 'firebase/firestore';
import type { ContentAgentTask } from '../types';

/**
 * Helper to convert task data to Firestore format
 */
function convertToFirestore(task: Record<string, unknown>): DocumentData {
  const result: Record<string, unknown> = { ...task };

  // Remove id field (stored as document ID, not in data)
  delete result.id;

  // Convert Date to Firestore Timestamp
  if (result.createdAt instanceof Date) {
    result.createdAt = Timestamp.fromDate(result.createdAt);
  }

  if (result.updatedAt instanceof Date) {
    result.updatedAt = Timestamp.fromDate(result.updatedAt);
  }

  if (result.cancelledAt instanceof Date) {
    result.cancelledAt = Timestamp.fromDate(result.cancelledAt);
  }

  return result as DocumentData;
}

export const backendTaskConverter: FirestoreDataConverter<ContentAgentTask> = {
  /**
   * Convert ContentAgentTask model to Firestore data
   * Called when writing to Firestore
   *
   * Handles conversion of Date fields to Firestore Timestamps:
   * - createdAt: Date → Timestamp
   * - updatedAt: Date → Timestamp
   * - cancelledAt: Date → Timestamp
   */
  toFirestore(
    task: WithFieldValue<ContentAgentTask> | PartialWithFieldValue<ContentAgentTask>,
    _options?: SetOptions
  ): DocumentData {
    return convertToFirestore(task as Record<string, unknown>);
  },

  /**
   * Convert Firestore data to ContentAgentTask model
   * Called when reading from Firestore
   *
   * Handles conversion of Firestore Timestamps to Date:
   * - createdAt: Timestamp → Date
   * - updatedAt: Timestamp → Date
   * - cancelledAt: Timestamp → Date
   */
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions
  ): ContentAgentTask {
    const data = snapshot.data(options);
    const result: Record<string, unknown> = { ...data, id: snapshot.id };

    // Convert Firestore Timestamps to Date
    if (result.createdAt && typeof (result.createdAt as Timestamp).toDate === 'function') {
      result.createdAt = (result.createdAt as Timestamp).toDate();
    }

    if (result.updatedAt && typeof (result.updatedAt as Timestamp).toDate === 'function') {
      result.updatedAt = (result.updatedAt as Timestamp).toDate();
    }

    if (result.cancelledAt && typeof (result.cancelledAt as Timestamp).toDate === 'function') {
      result.cancelledAt = (result.cancelledAt as Timestamp).toDate();
    }

    return result as unknown as ContentAgentTask;
  },
};

export default backendTaskConverter;
